import { z } from 'zod';

/**
 * Bot provisioning + support contracts (CLAUDE.md §3 `bot-provisioning`,
 * `support`). One bot per shop, routed by token. Managed Bots is the happy
 * path; the manual BotFather flow stays as a fallback because the API is young
 * (§9).
 */

export const botProvisioningSchema = z.enum(['managed', 'manual']);

export const botStateSchema = z.enum(['pending', 'active', 'revoked', 'error']);

export const abaListenerStateSchema = z.enum([
  'off',
  'awaiting_group',
  'missing_admin',
  'active',
  'error',
]);

export const shopBotSchema = z.object({
  id: z.string().uuid(),
  botUserId: z.number().int().nullable(),
  username: z.string().nullable(),
  provisioning: botProvisioningSchema,
  state: botStateSchema,
  /** Bot-to-Bot mode ON and Privacy Mode OFF are both required for rung 3. */
  b2bMode: z.boolean(),
  privacyModeOff: z.boolean(),
  abaGroupChatId: z.number().int().nullable(),
  abaGroupTitle: z.string().nullable(),
  abaListenerState: abaListenerStateSchema,
  lastError: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type ShopBotDto = z.infer<typeof shopBotSchema>;

/** Start the Managed Bots deep-link flow; returns the link to open. */
export const startBotProvisioningSchema = z.object({
  desiredName: z.string().min(3).max(64).optional(),
  desiredUsername: z.string().min(5).max(32).optional(),
});

/** Fallback path: merchant pastes a token they created in @BotFather. */
export const attachManualBotSchema = z.object({
  token: z.string().regex(/^\d{6,12}:[A-Za-z0-9_-]{30,}$/, 'Not a Telegram bot token'),
});

export const botHealthSchema = z.object({
  botId: z.string().uuid(),
  reachable: z.boolean(),
  webhookOk: z.boolean(),
  pendingUpdateCount: z.number().int().nullable(),
  abaListener: abaListenerStateSchema,
  issues: z.array(z.string()),
});

/* ------------------------------------------------------------------ */
/* Support tier 1 — deterministic, zero AI cost, every plan            */
/* ------------------------------------------------------------------ */

/**
 * The tier-1 brain answers from shop data through buttons and commands. It is
 * instant and never wrong, and it absorbs roughly 80% of repetitive questions
 * before any model is involved (§3 `support`).
 */
export const supportTopicSchema = z.enum([
  'order_status',
  'shipping_fee',
  'opening_hours',
  'return_policy',
  'contact_human',
  'faq',
]);
export type SupportTopic = z.infer<typeof supportTopicSchema>;

export const faqEntrySchema = z.object({
  id: z.string().uuid(),
  question: z.object({ km: z.string(), en: z.string() }),
  answer: z.object({ km: z.string(), en: z.string() }),
  position: z.number().int(),
  isActive: z.boolean(),
});

export const upsertFaqSchema = z.object({
  question: z.object({ km: z.string().max(200).default(''), en: z.string().max(200).default('') }),
  answer: z.object({ km: z.string().max(2000).default(''), en: z.string().max(2000).default('') }),
  position: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
});

/* ------------------------------------------------------------------ */
/* Reply kit (§6.3)                                                    */
/* ------------------------------------------------------------------ */

/**
 * One-tap copy of a product link plus ready-made Khmer reply text, so a
 * merchant answering a Facebook inbox beats typing sizes and colours by hand.
 */
export const replyKitSchema = z.object({
  productUrl: z.string().url(),
  shortText: z.object({ km: z.string(), en: z.string() }),
  fullText: z.object({ km: z.string(), en: z.string() }),
});
